/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669593600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 28 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "campaign-monitor",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "List subscriptions",
          "depth": 0,
          "id": "list-subscriptions"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record mappings",
          "depth": 1,
          "id": "record-mappings"
        }, {
          "value": "Field mappings",
          "depth": 1,
          "id": "field-mappings"
        }, {
          "value": "Custom mappings",
          "depth": 1,
          "id": "custom-mappings"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      code: "code",
      img: "img",
      h3: "h3",
      a: "a"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "To find your API keys, navigate to your Profile (top right of the page) > Account Settings > API Key and create a private API key."
      }), "\n", _jsx(_components.h2, {
        id: "list-subscriptions",
        children: "List subscriptions"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports automatically creating a new List for your sync. You can optionally assign a custom name to your list.\nIf no name is provided, Hightouch will default to using your model name."
      }), "\n", _jsxs(_components.p, {
        children: ["Here, the model name ", _jsx(_components.code, {
          children: "Subscription List 1"
        }), " will be used as the list name:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-campaignmonitor-create-list.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "Additionally, instead of creating a new list, Hightouch can also sync subscribers to existing lists that are already populated in your Campaign Monitor account."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-campaignmonitor-existing-list.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "This integration supports Segment mode. New records will be inserted into the Campaign Monitor\nList. Records that are removed from the model will be removed from the\nsegment."
      }), "\n", _jsx(_components.h3, {
        id: "record-mappings",
        children: "Record mappings"
      }), "\n", _jsx(_components.p, {
        children: "Records can be matched from your source to your Campaign Monitor Subscriptions by email."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-campaignmonitor-id-mappings.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Campaign Monitor's default fields."
      }), "\n", _jsxs(_components.p, {
        children: ["Note: You must provide a mapping for the \"Consent To Track\" field. More details can be found in the ", _jsx(_components.a, {
          href: "https://www.campaignmonitor.com/api/v3-3/subscribers/#importing-many-subscribers",
          children: "Campaign\nMonitor API docs."
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-campaignmonitor-field-mappings.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "custom-mappings",
        children: "Custom mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["You can also sync custom fields to Campaign Monitor's Subscription Lists. If you are syncing custom fields of\ndata type ", _jsx(_components.code, {
          children: "Multiple options - select many"
        }), ", ensure your column data is an array."]
      }), "\n", _jsxs(_components.p, {
        children: ["Note: Ensure the Campaign Monitor List has custom fields already defined in your account before syncing custom fields.\n", _jsx(_components.img, {
          src: "destinations/destination-campaignmonitor-custom-mappings.png",
          alt: ""
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
